import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks per-tab "unread since you last looked" counts and drives a browser tab-title cue.
 *
 * Each activity panel reports its current item count via `report(tabKey, count)` on every
 * (polled) fetch. A tab the user is currently viewing is treated as seen, so its unread stays 0
 * and its baseline follows the latest count. For a tab the user is NOT viewing, unread grows as
 * new items arrive; opening that tab marks it seen and clears the badge.
 *
 * The total unread across tabs is reflected in the document title as `(N) <original>` so a user
 * who has switched to another browser tab/window still sees that something changed. The title is
 * restored on unmount.
 */
export function useUnreadTabs(activeTab: string) {
  // Latest known count per tab, and the count at the moment the tab was last "seen".
  const counts = useRef<Record<string, number>>({});
  const seen = useRef<Record<string, number>>({});
  const [unread, setUnread] = useState<Record<string, number>>({});
  const activeRef = useRef(activeTab);
  activeRef.current = activeTab;

  const recompute = useCallback(() => {
    const next: Record<string, number> = {};
    for (const key of Object.keys(counts.current)) {
      const seenCount = seen.current[key] ?? counts.current[key];
      next[key] = Math.max(0, counts.current[key] - seenCount);
    }
    setUnread(next);
  }, []);

  /** A panel reports its current item count. */
  const report = useCallback(
    (tabKey: string, count: number) => {
      const isFirst = counts.current[tabKey] === undefined;
      counts.current[tabKey] = count;
      // First sight, or the active tab, establishes/advances the seen baseline (nothing unread).
      if (isFirst || tabKey === activeRef.current) {
        seen.current[tabKey] = count;
      }
      recompute();
    },
    [recompute],
  );

  // When the active tab changes, mark it seen (clear its badge).
  useEffect(() => {
    if (counts.current[activeTab] !== undefined) {
      seen.current[activeTab] = counts.current[activeTab];
      recompute();
    }
  }, [activeTab, recompute]);

  // Browser tab-title cue: total unread as a (N) prefix, restored on unmount.
  useEffect(() => {
    const total = Object.values(unread).reduce((a, b) => a + b, 0);
    const original = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = total > 0 ? `(${total}) ${original}` : original;
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [unread]);

  return { unread, report };
}
